<template>
  <div v-if="!hasDrinks" class="text-center">
    Aucune boisson disponible.
  </div>
  <div class="w-full" v-if="hasDrinks">
    <div class="text-xl font-bold text-gray-800 mb-4">
      {{title||"Sélectionnez vos boissons"}}
    </div>

    <div class="min-w-max flex gap-4 pb-4 overflow-x-auto">
      <div
        v-for="(choice, index) in drinks"
        :key="choice.id || index"
        class="border w-[35vw] rounded-2xl overflow-hidden flex-shrink-0"
      >
        <div class="w-full py-[35%] bg-slate-100 relative">
          <div class="absolute w-full h-full top-0 left-0">
            <ImgComponent
              :src="choice.image"
              class="w-full h-full object-cover"
              :fallback="'/images/placeholders/restaurant-dishes.png'"
            />
          </div>
        </div>
        <div class="p-2 text-center">
          <div class="text-gray-800 font-bold">
            {{ (choice.amount || choice.price) || 0 }} FCFA
          </div>
          <div class="w-full truncate text-sm text-gray-600">
            {{ choice.name }}
          </div>
        </div>
        <div class="pl-2 pb-2 flex gap-2 justify-center items-center relative">
          <div
            class="overflow-hidden duration-500 rounded-lg ripple w-10 h-10 text-lg border border-[#F4F4F5] bg-[#F4F4F5] items-center justify-center flex"
            :class="{
              'opacity-50': choiceSelectionData(choice.id).number === 0
            }"
            @click="decreaseOptionSelection(choice)"
          >
            <div>-</div>
          </div>
          <div
            class="text-gray-800 overflow-hidden duration-500 w-6 h-8 text-lg items-center justify-center flex"
          >
            <div>{{ choiceSelectionData(choice.id).number || 0 }}</div>
          </div>
          <div
            class="overflow-hidden duration-500 rounded-lg ripple w-10 h-10 text-lg border border-[#F4F4F5] bg-[#F4F4F5] items-center justify-center flex"
            @click="increaseOptionSelection(choice)"
          >
            <div>+</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { bus } from '@core/events/eventSystem';
import ImgComponent from '@ui/core/img/ImgComponent.global.vue';

const props = defineProps({
  dishData: {
    type: Object,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  restaurantId: {
    type: String,
    default: ''
  },
  selectedDrinks: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:drinks']);

const selectedDrinks = ref({});
const drinksData = ref([]);
const loading = ref(false);

// Computed
const hasDrinks = computed(() => {
  return drinks.value && drinks.value.length > 0;
});

const drinks = computed(() => {
  return drinksData.value || [];
});

// Methods
const choiceSelectionData = (choiceId) => {
  return selectedDrinks.value[choiceId] || { number: 0 };
};

const increaseOptionSelection = (choice) => {
  const current = choiceSelectionData(choice.id);

  selectedDrinks.value[choice.id] = {
    number: current.number + 1,
    drink: choice // Changed from 'choice' to 'drink' to match emitDrinks
  };

  emitDrinks();
};

const decreaseOptionSelection = (choice) => {
  const current = choiceSelectionData(choice.id);

  if (current.number <= 1) {
    delete selectedDrinks.value[choice.id];
  } else {
    selectedDrinks.value[choice.id] = {
      ...current,
      number: current.number - 1
    };
  }

  emitDrinks();
};

const emitDrinks = () => {
  // Convert selectedDrinks object to array format expected by API
  const drinksArray = Object.values(selectedDrinks.value).map((selection) => {
    const { drink, number: quantity } = selection;
    const price = drink.price || 0;

    return {
      drinkId: drink.id,
      name: drink.name,
      price: drink.amount || drink.price || 0,
      quantity: quantity,
      total: (drink.amount || drink.price || 0) * quantity
    };
  });

  emit('update:drinks', drinksArray);
};

const loadDrinks = async () => {
  if (!props.restaurantId) return;

  loading.value = true;

  try {
    // Fetch dedicated restaurant drinks
    const res = await new Promise((resolve) => {
      const requestId = 'restaurantDetail-drinks-' + Date.now();
      // Use the dedicated drinks endpoint
      let url = window.$constants?.ENDPOINTS?.RESTAURANTS?.DRINKS?.replace('{restaurantId}', props.restaurantId);
      
      
      const onResponse = (response) => {
        if (response.id === requestId) {
          bus.off('network.response', onResponse);
          resolve(response);
        }
      };
      
      bus.on('network.response', onResponse);
      bus.emit('network.request', { id: requestId, url, method: 'GET' });
      
      setTimeout(() => {
        bus.off('network.response', onResponse);
        resolve(null);
      }, 10000);
    });

    console.log('[DrinkChoices.global] loadDrinks response:', res);

    
    if (res?.data?.data?.drinks && Array.isArray(res?.data?.data?.drinks)) {
        drinksData.value = res?.data?.data?.drinks;
        return;
      
    }

    // Fallback to dishData.drinks
    if (props.dishData?.drinks) {
      drinksData.value = props.dishData.drinks;
    } else {
      drinksData.value = [];
    }
  } catch (error) {
    console.error('Failed to load drinks:', error);
    drinksData.value = props.dishData?.drinks || [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadDrinks();
});

// Initialize selectedDrinks from selectedDrinks prop
watch(
  () => props.selectedDrinks,
  (newDrinks) => {
    if (!newDrinks || newDrinks.length === 0) {
      selectedDrinks.value = {};
      return;
    }

    // Convert selectedDrinks array to selectedDrinks object format
    const initialized = {};
    newDrinks.forEach((drink) => {
      const id = drink.drinkId || drink.id;
      if (id) {
        initialized[id] = {
          number: drink.quantity || 1,
          drink: {
            id: id,
            name: drink.name || '',
            price: drink.price || drink.amount || 0
          }
        };
      }
    });

    // Only update if different to avoid infinite loop
    if (JSON.stringify(selectedDrinks.value) !== JSON.stringify(initialized)) {
      selectedDrinks.value = initialized;
    }
  },
  { immediate: true, deep: true }
);
</script>
